import { useState } from "react";
import { COMPANY_INFO } from "../data/siteData";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    website_url: "" // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Indtast venligst dit navn";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Indtast venligst dit telefonnummer";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Indtast venligst din e-mailadresse";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Indtast venligst en gyldig e-mailadresse";
    }
    if (!formData.message.trim()) {
      newErrors.message = "Skriv venligst en kort beskrivelse af din opgave";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");

    if (!validate()) return;

    setSubmitting(true);

    try {
      const response = await fetch("https://api.dev.auxo.dkwebform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url // Honeypot
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: "",
          phone: "",
          email: "",
          message: "",
          website_url: ""
        });
      } else {
        const resData = await response.json().catch(() => ({}));
        setServerError(
          resData.message || "Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring til os direkte."
        );
      }
    } catch (err) {
      setServerError("Kunne ikke forbinde til formulartjenesten. Ring venligst på 26 19 20 88.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border-t-4 border-[#ea580c] shadow-2xl p-6 sm:p-8 border-x border-b border-stone-200">
      
      {submitted ? (
        <div className="text-center py-12 space-y-4 animate-fadeIn">
          <div className="w-16 h-16 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto text-2xl">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h3 className="text-2xl font-black uppercase text-slate-900">Mange tak for din henvendelse!</h3>
          <p className="text-slate-600 max-w-md mx-auto text-sm sm:text-base">
            Vi har modtaget din besked og vender tilbage hurtigst muligt med et uforpligtende tilbud eller rådgivning.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="mt-4 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold uppercase tracking-wider px-6 py-3 rounded-md transition-colors"
          >
            <span>Send en ny besked</span>
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          
          <div className="space-y-1">
            <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-slate-900">
              Få et uforpligtende tilbud
            </h3>
            <p className="text-slate-600 text-xs sm:text-sm">
              Udfyld venligst formularen herunder, så vender vi hurtigt tilbage til dig.
            </p>
          </div>

          {/* Mandatory Honeypot Anti-Spam Field */}
          <div className="sr-only absolute top-0 left-0 h-0 w-0 overflow-hidden pointer-events-none" aria-hidden="true">
            <label htmlFor="website_url">Hjemmeside URL (udfyld ikke)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              tabIndex={-1}
              value={formData.website_url}
              onChange={handleChange}
              autoComplete="off"
            />
          </div>

          {serverError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-xs flex items-center gap-2">
              <i className="fa-solid fa-triangle-exclamation shrink-0 text-red-500" aria-hidden="true" />
              <span>{serverError}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            {/* Navn */}
            <div className="space-y-1">
              <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-slate-800">
                Dit navn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="f.eks. Peter Hansen"
                className={`w-full px-3.5 py-2.5 rounded border text-sm text-slate-900 bg-stone-50 focus:outline-none focus:ring-1 focus:bg-white transition-all ${
                  errors.name ? "border-red-500 focus:ring-red-200" : "border-slate-300 focus:ring-[#ea580c] focus:border-[#ea580c]"
                }`}
              />
              {errors.name && <p className="text-[11px] text-red-600 mt-0.5">{errors.name}</p>}
            </div>

            {/* Telefonnummer */}
            <div className="space-y-1">
              <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-slate-800">
                Dit telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="f.eks. 26 19 20 88"
                className={`w-full px-3.5 py-2.5 rounded border text-sm text-slate-900 bg-stone-50 focus:outline-none focus:ring-1 focus:bg-white transition-all ${
                  errors.phone ? "border-red-500 focus:ring-red-200" : "border-slate-300 focus:ring-[#ea580c] focus:border-[#ea580c]"
                }`}
              />
              {errors.phone && <p className="text-[11px] text-red-600 mt-0.5">{errors.phone}</p>}
            </div>

          </div>

          {/* Email */}
          <div className="space-y-1">
            <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-800">
              Din e-mail <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="din@email.dk"
              className={`w-full px-3.5 py-2.5 rounded border text-sm text-slate-900 bg-stone-50 focus:outline-none focus:ring-1 focus:bg-white transition-all ${
                errors.email ? "border-red-500 focus:ring-red-200" : "border-slate-300 focus:ring-[#d96b27] focus:border-[#d96b27]"
              }`}
            />
            {errors.email && <p className="text-[11px] text-red-600 mt-0.5">{errors.email}</p>}
          </div>

          {/* Beskrivelse */}
          <div className="space-y-1">
            <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-slate-800">
              Beskriv opgaven <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={4}
              value={formData.message}
              onChange={handleChange}
              placeholder="Fortæl os kort om projektets omfang, placering, ønsker til opstart mv."
              className={`w-full px-3.5 py-2.5 rounded border text-sm text-slate-900 bg-stone-50 focus:outline-none focus:ring-1 focus:bg-white transition-all resize-y ${
                errors.message ? "border-red-500 focus:ring-red-200" : "border-slate-300 focus:ring-[#ea580c] focus:border-[#ea580c]"
              }`}
            />
            {errors.message && <p className="text-[11px] text-red-600 mt-0.5">{errors.message}</p>}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#ea580c] hover:bg-[#c2410c] text-white font-extrabold uppercase tracking-wider text-xs sm:text-sm py-3.5 rounded-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 shadow-md"
          >
            {submitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin text-base" aria-hidden="true" />
                <span>Sender besked...</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                <span>Indhent tilbud</span>
              </>
            )}
          </button>

          <p className="text-center text-[11px] text-slate-500">
            Felter markeret med <span className="text-red-500">*</span> skal udfyldes. Du kan også ringe direkte på{" "}
            <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="font-bold text-slate-900 underline whitespace-nowrap">
              {COMPANY_INFO.phone}
            </a>
          </p>

        </form>
      )}

    </div>
  );
}
